import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  BookOpen,
  Calendar,
  CheckCircle2,
  Circle,
  Link2,
  Repeat,
  GraduationCap,
  StickyNote,
  ListChecks,
} from "lucide-react";
import { formatDate, formatDisplayDate, isPast } from "../utils/dateUtils";
import { renderRichText } from "../utils/richText";

const UPCOMING_DATE_KEY = "reminders_upcoming_until_date";
const DEFAULT_UPCOMING_DAYS = 10;

const Reminders = ({
  tabs = [],
  subGoals = [],
  quickLinkItems = [],
  recurringItems = [],
  rememberItems = [],
  conferenceItems = [],
  publicationItems = [],
  stickyNoteItems = [],
  onToggleGoal,
  onToggleSubGoal,
  onToggleQuickLink,
  onToggleRecurringOccurrence,
  onToggleRemember,
  onToggleConference,
  onTogglePublication,
  onToggleStickyNote,
  onUpdateGoalDeadline,
  onUpdateSubGoalDeadline,
  onUpdateQuickLinkDueDate,
  onUpdateRememberDueDate,
}) => {
  const [upcomingUntilDate, setUpcomingUntilDate] = useState(() => {
    return localStorage.getItem(UPCOMING_DATE_KEY) || "";
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (upcomingUntilDate) {
      localStorage.setItem(UPCOMING_DATE_KEY, upcomingUntilDate);
    } else {
      localStorage.removeItem(UPCOMING_DATE_KEY);
    }
  }, [upcomingUntilDate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const goalLookup = {};
  (tabs || []).forEach((tab) => {
    if (tab && tab.goals && Array.isArray(tab.goals)) {
      tab.goals.forEach((goal) => {
        goalLookup[goal.id] = {
          text: goal.text,
          tabName: tab.name,
          tabId: tab.id,
        };
      });
    }
  });

  const allItemsWithDeadlines = [];

  (tabs || []).forEach((tab) => {
    if (tab && tab.goals && Array.isArray(tab.goals)) {
      tab.goals.forEach((goal) => {
        if (goal.deadline && !goal.completed) {
          allItemsWithDeadlines.push({
            ...goal,
            tabName: tab.name,
            tabId: tab.id,
            isSubGoal: false,
          });
        }
      });
    }
  });

  (subGoals || []).forEach((sg) => {
    if (sg.deadline && !sg.completed) {
      const parent = goalLookup[sg.goalId];
      if (parent) {
        allItemsWithDeadlines.push({
          ...sg,
          tabName: parent.tabName,
          tabId: parent.tabId,
          isSubGoal: true,
          parentGoalName: parent.text,
        });
      }
    }
  });

  (quickLinkItems || []).forEach((q) => {
    if (q.deadline && !q.completed) {
      allItemsWithDeadlines.push({
        ...q,
        isSubGoal: false,
        isQuickLink: true,
      });
    }
  });

  (recurringItems || []).forEach((r) => {
    if (r.deadline) {
      allItemsWithDeadlines.push({
        ...r,
        isSubGoal: false,
        isRecurring: true,
      });
    }
  });

  (rememberItems || []).forEach((r) => {
    if (r.deadline && !r.completed) {
      allItemsWithDeadlines.push({
        ...r,
        isSubGoal: false,
        isRemember: true,
      });
    }
  });

  (conferenceItems || []).forEach((c) => {
    if (c.deadline) {
      allItemsWithDeadlines.push({
        ...c,
        isSubGoal: false,
        isConference: true,
        tabName: "Conference",
      });
    }
  });

  (publicationItems || []).forEach((p) => {
    if (p.deadline) {
      allItemsWithDeadlines.push({
        ...p,
        isSubGoal: false,
        isPublication: true,
        tabName: "Publication",
      });
    }
  });

  (stickyNoteItems || []).forEach((n) => {
    if (n.deadline) {
      allItemsWithDeadlines.push({
        ...n,
        isSubGoal: false,
        isStickyNote: true,
        tabName: "Note",
      });
    }
  });

  allItemsWithDeadlines.sort((a, b) => {
    const dateA = new Date(a.deadline);
    const dateB = new Date(b.deadline);
    return dateA - dateB;
  });

  const todayStr = formatDate(new Date());

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = formatDate(tomorrow);

  const pastItems = allItemsWithDeadlines.filter((g) =>
    isPast(new Date(g.deadline)),
  );
  const todayItems = allItemsWithDeadlines.filter(
    (g) => formatDate(new Date(g.deadline)) === todayStr,
  );
  const tomorrowItems = allItemsWithDeadlines.filter(
    (g) => formatDate(new Date(g.deadline)) === tomorrowStr,
  );

  let upcomingCutoff;
  if (upcomingUntilDate) {
    const cutoff = new Date(upcomingUntilDate);
    cutoff.setHours(23, 59, 59, 999);
    upcomingCutoff = cutoff;
  } else {
    const defaultCutoff = new Date();
    defaultCutoff.setDate(defaultCutoff.getDate() + 1 + DEFAULT_UPCOMING_DAYS);
    defaultCutoff.setHours(23, 59, 59, 999);
    upcomingCutoff = defaultCutoff;
  }

  const upcomingItems = allItemsWithDeadlines.filter((g) => {
    const goalDateStr = formatDate(new Date(g.deadline));
    if (goalDateStr === todayStr || goalDateStr === tomorrowStr) return false;
    if (isPast(new Date(g.deadline))) return false;
    const goalDate = new Date(g.deadline);
    goalDate.setHours(0, 0, 0, 0);
    return goalDate <= upcomingCutoff;
  });

  // Note: We intentionally do NOT early-return null when all sections are
  // empty. The "Upcoming" section is always shown (with a date-range picker)
  // so the user can always extend the upcoming window even when nothing
  // currently falls inside it.

  const handleToggle = (item) => {
    if (item.isStickyNote) {
      onToggleStickyNote && onToggleStickyNote(item.noteId, item.lineIndex);
    } else if (item.isPublication) {
      onTogglePublication && onTogglePublication(item.pubId, item.kind);
    } else if (item.isConference) {
      onToggleConference && onToggleConference(item.cardId, item.entryId);
    } else if (item.isRecurring) {
      onToggleRecurringOccurrence &&
        onToggleRecurringOccurrence(item.id, item.deadline);
    } else if (item.isRemember) {
      onToggleRemember && onToggleRemember(item.id);
    } else if (item.isQuickLink) {
      onToggleQuickLink && onToggleQuickLink(item.id);
    } else if (item.isSubGoal) {
      onToggleSubGoal && onToggleSubGoal(item.id);
    } else {
      onToggleGoal && onToggleGoal(item.tabId, item.id);
    }
  };

  const handleDeadlineChange = (item, newDeadline) => {
    const value = newDeadline || null;
    if (item.isRemember) {
      onUpdateRememberDueDate && onUpdateRememberDueDate(item.id, value);
    } else if (item.isQuickLink) {
      onUpdateQuickLinkDueDate && onUpdateQuickLinkDueDate(item.id, value);
    } else if (item.isSubGoal) {
      onUpdateSubGoalDeadline && onUpdateSubGoalDeadline(item.id, value);
    } else {
      onUpdateGoalDeadline && onUpdateGoalDeadline(item.tabId, item.id, value);
    }
  };

  const renderItem = (item) => {
    const accent = item.isStickyNote
      ? "text-yellow-400"
      : item.isPublication
      ? "text-purple-400"
      : item.isRecurring
        ? "text-purple-400"
        : item.isConference
          ? "text-amber-400"
          : item.isRemember
            ? "text-cyan-400"
            : item.isQuickLink
              ? "text-cyan-400"
              : item.isSubGoal
                ? "text-orange-400"
                : "text-cyan-400";
    return (
      <div
        key={`${
          item.isStickyNote
            ? "sn"
            : item.isPublication
            ? "pb"
            : item.isRecurring
              ? "rc"
              : item.isConference
                ? "cf"
                : item.isRemember
                  ? "rm"
                  : item.isQuickLink
                    ? "ql"
                    : item.isSubGoal
                      ? "sg"
                      : "g"
        }-${item.id}`}
        className="flex items-center gap-2 bg-gray-700/50 p-2 rounded-lg group"
      >
        <button
          onClick={() => handleToggle(item)}
          className="flex-shrink-0 opacity-60 group-hover:opacity-100 transition-opacity"
          title={
            item.isNoteTask
              ? "Tick this item in its sticky note"
              : item.isStickyNote
              ? "Dismiss this reminder (clears the note's due date)"
              : item.isPublication
              ? "Dismiss this reminder (clears the date on the paper)"
              : item.isConference
                ? "Mark this submission submitted"
                : item.isRecurring
                  ? "Mark this occurrence done"
                  : "Mark as completed"
          }
        >
          {item.completed ? (
            <CheckCircle2 className="w-4 h-4 text-yellow-400" />
          ) : (
            <Circle className={`w-4 h-4 ${accent}`} />
          )}
        </button>
        {item.isStickyNote ? (
          <StickyNote className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : item.isPublication ? (
          <BookOpen className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : item.isRecurring ? (
          <Repeat className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : item.isConference ? (
          <GraduationCap className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : item.isRemember ? (
          <ListChecks className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : item.isQuickLink ? (
          <Link2 className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        ) : (
          <Calendar className={`w-4 h-4 flex-shrink-0 ${accent}`} />
        )}
        <div className="flex-1 min-w-0 flex items-baseline gap-1">
          <span className="text-cyan-200 text-sm truncate">
            {renderRichText(item.text, `rmd-${item.id}`, {
              collaborators: !(
                item.isStickyNote ||
                item.isPublication ||
                item.isConference ||
                item.isRemember ||
                item.isQuickLink
              ),
            })}
          </span>
          {item.isSubGoal && (
            <span
              className="text-orange-400/70 text-xs truncate flex-shrink"
              title={`SubGoal of ${item.parentGoalName}`}
            >
              (SG - {renderRichText(item.parentGoalName, `rmd-p-${item.id}`, { collaborators: true })})
            </span>
          )}
          {item.isQuickLink && (
            <span className="text-cyan-400/70 text-xs truncate flex-shrink">
              (Quick Link)
            </span>
          )}
          {item.isRemember && (
            <span className="text-cyan-400/70 text-xs truncate flex-shrink">
              (Remember)
            </span>
          )}
          {item.isRecurring && (
            <span className="text-purple-400/70 text-xs truncate flex-shrink">
              (Recurring)
            </span>
          )}
          {item.isConference && (
            <span className="text-amber-400/70 text-xs truncate flex-shrink">
              (Conference)
            </span>
          )}
          {item.isPublication && (
            <span className="text-purple-400/70 text-xs truncate flex-shrink">
              (Publication)
            </span>
          )}
          {item.isStickyNote && (
            <span className="text-yellow-400/70 text-xs truncate flex-shrink">
              (Note)
            </span>
          )}
        </div>
        {item.isRecurring ||
        item.isConference ||
        item.isPublication ||
        item.isStickyNote ? (
          // Computed/owned elsewhere; not directly editable in Reminders.
          <span className="text-gray-400 text-xs flex-shrink-0">
            {formatDisplayDate(new Date(item.deadline))}
          </span>
        ) : (
          <label
            className="relative flex-shrink-0 cursor-pointer"
            title="Click to change deadline"
          >
            <span className="text-gray-400 text-xs hover:text-cyan-300 transition-colors underline decoration-dotted decoration-gray-500 underline-offset-2">
              {formatDisplayDate(new Date(item.deadline))}
            </span>
            <input
              type="date"
              value={item.deadline}
              onChange={(e) => handleDeadlineChange(item, e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </label>
        )}
        <span className="text-gray-500 text-xs flex-shrink-0">
          ({item.tabName})
        </span>
      </div>
    );
  };

  const dayAfterTomorrow = new Date();
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  const minDateStr = formatDate(dayAfterTomorrow);

  return (
    <div className="bg-gray-800 rounded-3xl p-6 border-2 border-cyan-500/30 shadow-lg mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Bell className="w-5 h-5 text-cyan-400" />
        <h3 className="text-xl font-bold text-cyan-400">Reminders</h3>
      </div>

      <div className="space-y-4">
        {pastItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-yellow-400 mb-2">Past</h4>
            <div className="space-y-2">{pastItems.map(renderItem)}</div>
          </div>
        )}

        {todayItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-yellow-400 mb-2">
              Today
            </h4>
            <div className="space-y-2">{todayItems.map(renderItem)}</div>
          </div>
        )}

        {tomorrowItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-yellow-400 mb-2">
              Tomorrow
            </h4>
            <div className="space-y-2">{tomorrowItems.map(renderItem)}</div>
          </div>
        )}

        {/* Upcoming is always rendered (even when empty) so the user can
            always change the upcoming date range. */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h4 className="text-sm font-semibold text-yellow-400">Upcoming</h4>
            <div ref={datePickerRef} className="relative flex items-center">
              {upcomingUntilDate ? (
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="flex items-center gap-1 text-xs bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded-full hover:bg-cyan-500/30 transition-colors"
                  title="Change or clear upcoming date"
                >
                  <Calendar className="w-3 h-3" />
                  <span>
                    until {formatDisplayDate(new Date(upcomingUntilDate))}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="text-gray-400 hover:text-cyan-400 transition-colors"
                  title="Set upcoming date range"
                >
                  <Calendar className="w-4 h-4" />
                </button>
              )}
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-1 z-50 bg-gray-700 border border-gray-600 rounded-lg p-2 shadow-xl flex flex-col gap-2">
                  <input
                    type="date"
                    value={upcomingUntilDate}
                    min={minDateStr}
                    onChange={(e) => {
                      setUpcomingUntilDate(e.target.value);
                      if (e.target.value) setShowDatePicker(false);
                    }}
                    className="bg-gray-800 text-cyan-200 text-xs border border-gray-600 rounded px-2 py-1 focus:outline-none focus:border-cyan-500"
                  />
                  {upcomingUntilDate && (
                    <button
                      onClick={() => {
                        setUpcomingUntilDate("");
                        setShowDatePicker(false);
                      }}
                      className="text-xs text-red-400 hover:text-red-300 transition-colors"
                    >
                      Clear (default {DEFAULT_UPCOMING_DAYS} days)
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {upcomingItems.length > 0 ? (
            <div className="space-y-2">{upcomingItems.map(renderItem)}</div>
          ) : (
            <div className="text-xs text-gray-400 italic bg-gray-700/30 rounded-lg px-3 py-2">
              No upcoming reminders
              {upcomingUntilDate
                ? ` until ${formatDisplayDate(new Date(upcomingUntilDate))}`
                : ` in the next ${DEFAULT_UPCOMING_DAYS} days`}
              . Click the calendar icon to change the range.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reminders;
